import * as THREE from "three";
import debug from "debug";

const log = debug("app:gsg");

/**
 * The `MakeGsg` class is responsible for creating and managing Ground Signal Ground (GSG) components.
 * It listens for events, processes port connections, and emits structured data for visualization.
 * @category Experience
 * @subcategory World
 */
class MakeGsg {
  /**
   * Creates an instance of `MakeGsg`.
   * @param {object} experience - The experience object containing the scene, event hub, and port vault.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;
    this.portVault = this.experience.portVault.portVaultMap;
    log("MakeGsg initialized");
    this.setEventListeners();
  }

  cleanup() {
    try {
        if (this.eventHub) {
            this.eventHub.off("gsg-switch", this.init);
        }

        if (this.scene) {
            const objectsToRemove = [];
            this.scene.traverse((object) => {
                if (
                    object.name === "XMO" &&
                    (object.userData.layer === 40 || object.userData.layer === 50)
                ) {
                    objectsToRemove.push(object);
                }
            });

            objectsToRemove.forEach((object) => {
                this.scene.remove(object);
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach((mat) => mat.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        }

        log("Cleanup completed successfully.");
    } catch (error) {
        console.error("Error during cleanup:", error);
    }
}

  /**
   * Sets up event listeners for GSG-related actions.
   */
  setEventListeners() {
    /*this.eventHub.on("3d-switch", () => {
      log("3D switch event triggered");
      this.clearGSG();
    });*/
    this.eventHub.on("gsg-switch", this.init.bind(this));
  }

  /**
   * Initializes the GSG process when the corresponding event is triggered.
   * @param {object} event - The event object containing the enabled status.
   */
  async init(event) {
    try {
      this.clearGSG();
      if (event.enabled) {
        log("Starting GSG creation");

        const jumperXMDs = []; // Array to collect all jumper objects
        const anchorXMDs = []; // Array to collect all anchor objects

        this.portVault.forEach(({ port1, port2 }) => {
          const commonPosition = this.getPortPosition(port1, port2);

          // Collect jumper objects
          jumperXMDs.push(this.createJumperXMD(commonPosition, port1));

          // Collect anchor objects
          anchorXMDs.push(
            this.createAnchorXMD(commonPosition, port1, port1.parent)
          );
          anchorXMDs.push(
            this.createAnchorXMD(commonPosition, port2, port2.parent)
          );
        });

        // Emit the events once with all jumpers and anchors
        this.eventHub.emit("MakePartsEvent", {
          eventType: "auto",
          data: {
            figures: jumperXMDs,
            version: "2.0",
          },
        });

        log("anchorXNDs", anchorXMDs);

        this.eventHub.emit("gsg-collision", {
          figures: anchorXMDs,
          version: "2.0",
        });
      } else {
        this.clearGSG();
      }
    } catch (error) {
      log("Error in GSG init:", error);
    }
  }

  /**
   * Computes the common world position of two connected ports.
   * @param {object} port1 - The first port object.
   * @param {object} port2 - The second port object.
   * @returns {THREE.Vector3} - The computed world position.
   */
  getPortPosition(port1, port2) {
    try {
      const worldPosition = new THREE.Vector3();
      port1.group.getWorldPosition(worldPosition);

      // Round the values to the nearest whole number
      worldPosition.x = Math.round(worldPosition.x);
      worldPosition.y = Math.round(worldPosition.y);
      worldPosition.z = Math.round(worldPosition.z);

      return worldPosition;
    } catch (error) {
      log("Error getting common position:", error);
      return new THREE.Vector3();
    }
  }

  /**
   * Retrieves the world direction of a given port, considering its parent transformations.
   * @param {object} port - The port object.
   * @returns {THREE.Vector3} - The computed direction vector.
   */
  getPortDirection(port) {
    try {
      // Clone the port's direction vector
      const portDirection = port.group.direction.clone();

      // Get the parent's world quaternion
      const worldQuaternion = new THREE.Quaternion();
      port.group.parent.getWorldQuaternion(worldQuaternion);

      // Convert the quaternion to Euler angles to get rotation in degrees
      const euler = new THREE.Euler().setFromQuaternion(worldQuaternion, "YZX");
      const yaw = THREE.MathUtils.radToDeg(euler.y); // Y-axis (yaw) rotation in degrees

      log(`Y-axis rotation (yaw): ${yaw} degrees`);

      log(
        "Port direction before transform",
        portDirection,
        "for pn",
        port.parent
      );

      // Apply the parent's world quaternion to the direction vector
      portDirection.applyQuaternion(worldQuaternion).normalize();

      // Log the transformed port direction after applying the quaternion
      log(
        `Transformed Port Direction: x=${portDirection.x}, y=${portDirection.y}, z=${portDirection.z}`
      );

      // Round the values to avoid floating-point inaccuracies
      portDirection.x = Math.round(portDirection.x * 1e10) / 1e10;
      portDirection.y = Math.round(portDirection.y * 1e10) / 1e10;
      portDirection.z = Math.round(portDirection.z * 1e10) / 1e10;

      log(
        "Final Transformed Port direction after rounding",
        portDirection,
        "for parent",
        port.parent
      );
      return portDirection;
    } catch (error) {
      log("Error getting port direction:", error);
      return new THREE.Vector3();
    }
  }

  /**
   * Calculates the rotation angle needed for alignment between two directions.
   * @param {THREE.Vector3} connectingObjectDirection - The direction of the connecting object.
   * @param {THREE.Vector3} portWorldDirection - The world direction of the port.
   * @returns {number} - The calculated rotation angle in degrees.
   */
  calculateRotation(connectingObjectDirection, portWorldDirection) {
    try {
      const angleRadians =
        Math.atan2(portWorldDirection.z, portWorldDirection.x) -
        Math.atan2(connectingObjectDirection.z, connectingObjectDirection.x);
      return THREE.MathUtils.radToDeg(angleRadians);
    } catch (error) {
      log("Error calculating rotation:", error);
      return 0;
    }
  }

  /**
   * Creates a jumper object to be placed in the scene.
   * @param {THREE.Vector3} position - The position of the jumper.
   * @param {object} port - The port associated with the jumper.
   * @returns {object|null} - The jumper object data.
   */
  createJumperXMD(position, port) {
    try {
      const portWorldDirection = this.getPortDirection(port);
      const jumperRotation = this.calculateRotation(
        new THREE.Vector3(1, 0, 0), // Default direction for jumper
        portWorldDirection
      );
      return {
        partData: {
          mode: "xm-part",
          fullpartnumber: "XM-GSGJ",
        },
        x: position.x,
        z: position.z,
        angle: jumperRotation,
      };
    } catch (error) {
      log("Error creating jumper XMD:", error);
      return null;
    }
  }

  /**
   * Creates an anchor object for connecting GSG components.
   * @param {THREE.Vector3} position - The position of the anchor.
   * @param {object} port - The associated port object.
   * @param {object} parent - The parent component of the anchor.
   * @returns {object|null} - The anchor object data.
   */
  createAnchorXMD(position, port, parent) {
    try {
      const portWorldDirection = this.getPortDirection(port);
      const anchorDirection = new THREE.Vector3(0, 0, -1); // Default anchor direction
      const rotation = this.calculateRotation(
        anchorDirection,
        portWorldDirection
      );
      return {
        partData: {
          mode: "xm-part",
          fullpartnumber: "XM-ANCHOR2",
          parent: parent || "no result",
        },
        x: position.x,
        z: position.z,
        angle: rotation,
      };
    } catch (error) {
      log("Error creating anchor XMD:", error);
      return null;
    }
  }

  /**
   * Removes all GSG-related objects from the scene.
   */
  clearGSG() {
    try {
      const objectsToRemove = [];
      this.scene.traverse((object) => {
        if (
          object.name === "XMO" &&
          (object.userData.layer === 40 || object.userData.layer === 50)
        ) {
          objectsToRemove.push(object);
        }
      });
      objectsToRemove.forEach((object) => {
        this.scene.remove(object);
      });

      this.eventHub.emit("xmd");
      log("GSG cleared");
    } catch (error) {
      log("Error clearing GSG:", error);
    }
  }
}

export default MakeGsg;
